import './AlertMessage.scss';

import * as React from 'react';

import cx from 'classnames';

import { Badge } from '~shared/components/Badge';
import { FocusIndicator } from '~shared/components/FocusIndicator/FocusIndicator';
import {
  IconExclamation,
  IconExclamationTriangle,
  IconInformation,
} from '~shared/components/icons/large';
import { IconX } from '~shared/components/icons/standard';
import { AllOrNone } from '~shared/types';

type CloseProps = AllOrNone<{ onClose: () => void; closeLabel: string }>;

export type AlertMessageProps = {
  align?: 'center' | 'left';
  children: React.ReactNode;
  tone: AlertMessageTone;
  showIcon?: boolean;
  wrap?: boolean;
} & CloseProps;

export function AlertMessage({
  children,
  tone,
  showIcon,
  onClose,
  closeLabel,
  align = 'left',
  wrap = false,
}: AlertMessageProps) {
  const alertBoxClass = cx(
    'flex gap-2',
    align === 'center' && 'justify-center',
    align === 'left' && 'items-start',
    'p-4 rounded-4 alert-message',
    toneClassMap[tone].box,
    {
      'flex-wrap': wrap,
      'alert-message--promote': tone === 'promote',
      'alert-message--info': tone === 'info',
      'alert-message--caution': tone === 'caution',
      'alert-message--critical': tone === 'critical',
    }
  );

  return (
    <div className={alertBoxClass}>
      {showIcon ? <AlertIconContainer tone={tone} /> : null}
      <AlertTextContainer
        tone={tone}
        align={align}
        onClose={onClose}
        closeLabel={closeLabel}
      >
        <AlertTextSpan tone={tone}>{children}</AlertTextSpan>
      </AlertTextContainer>
      {onClose ? (
        <AlertClose onClose={onClose} closeLabel={closeLabel} tone={tone} />
      ) : null}
    </div>
  );
}

const toneClassMap = {
  promote: {
    box: 'bg-orange-50',
    icon: '',
    text: 'text-orange-900',
    button: '',
  },
  info: {
    box: 'bg-cyan-50',
    icon: 'text-blue-600',
    text: 'text-blue-900',
    button: 'text-cyan-700 hocus:text-cyan-600',
  },
  caution: {
    box: 'bg-amber-50',
    icon: 'text-amber-700',
    text: 'text-amber-900',
    button: 'text-amber-700 hocus:text-amber-600',
  },
  critical: {
    box: 'bg-red-50',
    icon: 'text-red-600',
    text: 'text-red-900',
    button: 'text-red-600 hocus:text-red-600',
  },
};

type AlertMessageTone = keyof typeof toneClassMap;

function AlertIcon({ tone }: { tone: AlertMessageTone }) {
  switch (tone) {
    case 'info':
      return <IconInformation />;

    case 'caution':
      return <IconExclamation />;

    case 'critical':
      return <IconExclamationTriangle />;

    default:
      return null;
  }
}

function AlertIconContainer({
  tone,
}: {
  tone: AlertMessageTone;
}): React.ReactElement {
  if (tone === 'promote') {
    return (
      <div className="flex items-center justify-center flex-none h-12 -mt-2 pl-1">
        <Badge tone="pro">Pro</Badge>
      </div>
    );
  } else {
    return (
      <div
        className={`relative flex-none w-12 h-12 -m-2 ${toneClassMap[tone].icon}`}
      >
        {tone !== 'critical' ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-9 h-9 rounded-round bg-white" />
          </div>
        ) : null}
        <div className="relative flex">
          <AlertIcon tone={tone} />
        </div>
      </div>
    );
  }
}

function AlertClose({
  tone,
  onClose,
  closeLabel,
}: {
  tone: AlertMessageTone;
  onClose: () => void;
  closeLabel: string;
}) {
  const alertCloseClass = cx(
    'group relative focus:z-10 flex flex-none p-0 m-0',
    'bg-transparent border-0 rounded-2 appearance-none outline-none',
    'cursor-pointer transition ease-in-out duration-150',
    toneClassMap[tone].button
  );

  return (
    <button
      className={alertCloseClass}
      type="button"
      onClick={() => onClose()}
      aria-label={closeLabel}
    >
      <FocusIndicator />
      <IconX />
    </button>
  );
}

function AlertTextContainer({
  align,
  showIcon,
  onClose,
  children,
}: AlertMessageProps) {
  const textContainerClass = cx(
    'contents',
    align === 'left' && 'flex-1',
    showIcon && 'pl-4',
    onClose && 'pr-4'
  );

  return <div className={textContainerClass}>{children}</div>;
}

export function AlertTextSpan({
  tone,
  children,
}: {
  tone: AlertMessageTone;
  children: React.ReactNode;
}) {
  const alertTextClass = cx(
    'contents font-sans text-base leading-8 capsize',
    toneClassMap[tone].text
  );

  return <div className={alertTextClass}>{children}</div>;
}
